import React, { useState, useEffect } from "react";
import "../../../css/FeeTaxConfig.css";
import { v4 as uuidv4 } from "uuid";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';

interface FeeLogItem {
  _id: string;
  product_id: string;
  base_screening_fee: number;
  gst_percentage: number;
  gst_amount?: number;
  total_amount: number;
  currency?: string;
  change_type?: string;
  updated_by_user_id?: string;
  updated_at: string;
}

export default function FeeConfiguration() {
  const [baseFee, setBaseFee] = useState("");
  const [gst, setGst] = useState("");
  const [errors, setErrors] = useState<{ baseFee?: string; gst?: string }>({});
  const [success, setSuccess] = useState<string | null>(null);

  // Fee History logs state
  const [logs, setLogs] = useState<FeeLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  // Convert inputs to numbers
  const base = parseFloat(baseFee) || 0;
  const gstValue = parseFloat(gst) || 0;

  // Discounts
  const feeDiscount = 0;

  // 1️⃣ Base after discount
  const baseAfterDiscount = Math.max(base - feeDiscount, 0);

  // 2️⃣ GST only on base after discount
  const gstAmount = (baseAfterDiscount * gstValue) / 100;

  // 3️⃣ Final Total
  const totalAmount = baseAfterDiscount + gstAmount;

  // Fetch Fee History Logs
  const fetchLogs = async (pageNumber = 1) => {
    try {
      setLogsLoading(true);
      setLogsError(null);
      const token = sessionStorage.getItem("accessToken");
      const res = await api.payment_api.get(
        `/pricing/products/logs/all?page=${pageNumber}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && Array.isArray(res.data.logs)) {
        setLogs(res.data.logs);
      } else {
        setLogs([]);
      }
    } catch (err: any) {
      console.error("Error fetching fee history logs:", err);
      setLogsError(err.message || "Failed to load fee history logs.");
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
  }, [page]);

  // ================================
  // FORM SUBMIT
  // ================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: typeof errors = {};
    if (baseFee.trim() === "") {
      newErrors.baseFee = "Base Screening Fee is required.";
    }
    if (gst.trim() === "") {
      newErrors.gst = "GST is required.";
    } else if (isNaN(gstValue) || gstValue < 0 || gstValue > 18) {
      newErrors.gst = "GST must be between 0 and 18.";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const adminData = sessionStorage.getItem("backendResponse");
    const admin = adminData ? JSON.parse(adminData) : null;
    const updatedByUserId = admin?._id;

    if (!updatedByUserId) {
      showToast("Admin ID missing. Please login again.", 'error');
      return;
    }

    setErrors({});

    const payload = {
      product_id: uuidv4(),
      base_screening_fee: base,
      gst_percentage: gstValue,
      total_amount: totalAmount,
      currency: "INR",
      updated_by_user_id: updatedByUserId,
    };

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: You are not logged in.", 'error');
        return;
      }

      await api.payment_api.post("/pricing/products", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess("Product added successfully!");
      setTimeout(() => setSuccess(null), 3000);

      setBaseFee("");
      setGst("");

      // Refresh Fee History logs immediately
      fetchLogs(1);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message ||
        "An error occurred while submitting the form.";
      showToast(`Error: ${errorMessage}`, 'error');
    }
  };

  return (
    <div className="fee-config-container">
      <div className="fee-config-main-title">
        <h2>Fee Configuration</h2>
      </div>
      <hr className="fee-config-divider" />

      <div className="fee-config-content-grid">
        {/* FORM SECTION */}
        <div className="fee-config-form-section">
          <form className="fee-config-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Base Screening Fee (₹)</label>
                <input
                  type="number"
                  value={baseFee}
                  onChange={(e) => setBaseFee(e.target.value)}
                  placeholder="Enter amount"
                  required
                />
                {errors.baseFee && <p className="error">{errors.baseFee}</p>}
              </div>

              <div className="form-group">
                <label>GST (%)</label>
                <input
                  type="number"
                  value={gst}
                  onChange={(e) => setGst(e.target.value)}
                  placeholder="0-18"
                  required
                />
                {errors.gst && <p className="error">{errors.gst}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <button type="submit" className="submit-btn">
                  Submit
                </button>
              </div>
            </div>

            {success && <p className="success">{success}</p>}
          </form>
        </div>

        {/* PREVIEW SECTION */}
        <div className="fee-config-preview-section">
          <h3 className="preview-title-heading">Preview</h3>

          <div className="preview-card">
            <div className="preview-header">
              <div className="preview-to-pay-title">
                <span className="pay-icon">🧮</span>
                <span className="pay-text">To Pay</span>
              </div>
              <div className="preview-big-amount">
                ₹{totalAmount.toFixed(2)}
              </div>
              <div className="preview-tax-subtext">
                (incl. all taxes)
              </div>
            </div>

            <div className="preview-body">
              <div className="preview-row">
                <span className="item-label">
                  Fee for Child Preventive Health<br />Screening
                </span>
                <span className="item-value">₹{baseAfterDiscount.toFixed(2)}</span>
              </div>

              <div className="preview-row">
                <span className="item-label">GST at {gstValue} %</span>
                <span className="item-value">₹{gstAmount.toFixed(2)}</span>
              </div>
            </div>

            <div className="preview-footer-row">
              <span className="total-label">Total Amount</span>
              <span className="total-value">₹{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* FEE HISTORY SECTION */}
      <div className="fee-history-section">
        <div className="fee-config-main-title">
          <h2>Fee History</h2>
        </div>
        <hr className="fee-config-divider" />

        <div className="fee-history-table-container">
          <table className="fee-history-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Payment Set Date</th>
                <th>Base Screening Fee</th>
                <th>GST (%)</th>
                <th>Total Fee</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
                    Loading fee history...
                  </td>
                </tr>
              ) : logsError ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#dc2626", padding: "24px" }}>
                    {logsError}
                  </td>
                </tr>
              ) : logs.length > 0 ? (
                logs.map((item, index) => {
                  const rowNum = (page - 1) * 20 + index + 1;
                  const dateStr = item.updated_at
                    ? new Date(item.updated_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "-";

                  return (
                    <tr key={item._id || index}>
                      <td>{rowNum}</td>
                      <td>{dateStr}</td>
                      <td>₹{item.base_screening_fee?.toLocaleString("en-IN") || item.base_screening_fee}</td>
                      <td>{item.gst_percentage}%</td>
                      <td className="font-bold">₹{item.total_amount?.toLocaleString("en-IN") || item.total_amount}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
                    No fee history logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
